import { useEffect, useState } from "react";
import { Plus, Edit3, Trash2, X, Sparkles, Award } from "lucide-react";
import { http } from "@/lib/api";
import { toast } from "sonner";

const RARITIES = [
  { value: "common", label: "Common" },
  { value: "rare", label: "Rare" },
  { value: "epic", label: "Epic" },
  { value: "legendary", label: "Legendary" },
];

const RARITY_BORDER = {
  common: "border-white/15",
  rare: "border-[#7DA9D4]/50",
  epic: "border-[#C087D4]/50",
  legendary: "border-[#D4A547]/70",
};

const EMPTY = { name: "", description: "", icon: "✨", color: "#C9A055", rarity: "common" };

export default function AdminBadges() {
  const [badges, setBadges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | "new" | badge object
  const [assignFor, setAssignFor] = useState(null); // badge object when assigning

  const reload = async () => {
    try {
      const { data } = await http.get("/admin/badges");
      setBadges(data || []);
    } catch {
      toast.error("Couldn't load badges.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  const seedStarters = async () => {
    if (!confirm("Seed the 20 curated starter badges? Existing badges won't be touched.")) return;
    try {
      const { data } = await http.post("/admin/badges/seed-defaults", {});
      toast.success(`Added ${data.created} starter badges.`);
      reload();
    } catch {
      toast.error("Seed failed.");
    }
  };

  const save = async (payload, isNew) => {
    try {
      if (isNew) {
        await http.post("/admin/badges", payload);
        toast.success("Badge created.");
      } else {
        await http.patch(`/admin/badges/${editing.id}`, payload);
        toast.success("Badge updated.");
      }
      setEditing(null);
      reload();
    } catch {
      toast.error("Save failed.");
    }
  };

  const del = async (badge) => {
    if (!confirm(`Delete "${badge.name}"? This also removes it from every user who has it.`)) return;
    try {
      await http.delete(`/admin/badges/${badge.id}`);
      toast.success("Badge deleted.");
      reload();
    } catch {
      toast.error("Delete failed.");
    }
  };

  return (
    <div data-testid="admin-badges-panel">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <div className="font-display text-2xl text-[#F4F1EA]">Badges</div>
          <div className="font-ui text-[10px] tracking-[0.22em] text-[#8a857d] mt-1">
            {badges.length} {badges.length === 1 ? "badge" : "badges"} · assignable to any user
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={seedStarters}
            data-testid="badges-seed-defaults-btn"
            className="btn-outline text-[10px] flex items-center gap-2"
          >
            <Sparkles size={12} /> Seed 20 Starter Badges
          </button>
          <button
            onClick={() => setEditing("new")}
            data-testid="badges-new-btn"
            className="btn-gold flex items-center gap-2"
          >
            <Plus size={14} /> New Badge
          </button>
        </div>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="text-[#8a857d] font-body">Loading…</div>
      ) : badges.length === 0 ? (
        <div className="border border-dashed border-white/10 bg-[#0a0a0a] p-10 text-center" data-testid="badges-empty">
          <Award size={32} className="mx-auto text-[#C9A055] opacity-50 mb-3" />
          <div className="font-display text-lg text-[#F4F1EA] mb-1">No badges yet</div>
          <div className="font-body text-sm text-[#8a857d] mb-4">
            Seed the 20 curated starters, or design your own from scratch.
          </div>
          <button onClick={seedStarters} className="btn-gold inline-flex items-center gap-2">
            <Sparkles size={14} /> Seed Starters
          </button>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {badges.map((b) => (
            <div
              key={b.id}
              data-testid={`badge-card-${b.slug}`}
              className={`border ${RARITY_BORDER[b.rarity] || "border-white/15"} bg-[#0a0a0a] p-4 flex flex-col`}
            >
              <div className="flex items-start gap-3 mb-3">
                <div
                  className="w-14 h-14 flex items-center justify-center text-2xl shrink-0 rounded-full"
                  style={{ background: `${b.color}22`, border: `1px solid ${b.color}` }}
                >
                  <span>{b.icon}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display text-[#F4F1EA] text-base truncate">{b.name}</div>
                  <div className="font-ui text-[9px] tracking-[0.22em] uppercase text-[#8a857d] mt-0.5">
                    {b.rarity} · assigned to {b.assigned_count || 0}
                  </div>
                </div>
              </div>
              <p className="font-body text-[13px] text-[#B5B0A7] leading-relaxed flex-1 mb-3 line-clamp-3">
                {b.description || "—"}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setEditing(b)}
                  data-testid={`badge-edit-${b.slug}`}
                  className="btn-outline flex-1 justify-center text-[10px]"
                >
                  <Edit3 size={11} /> Edit
                </button>
                <button
                  onClick={() => setAssignFor(b)}
                  data-testid={`badge-assign-${b.slug}`}
                  className="btn-outline justify-center text-[10px]"
                >
                  Assign
                </button>
                <button
                  onClick={() => del(b)}
                  data-testid={`badge-delete-${b.slug}`}
                  className="btn-outline justify-center text-[10px] border-red-700 text-red-400"
                >
                  <Trash2 size={11} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <BadgeEditor
          initial={editing === "new" ? EMPTY : editing}
          onClose={() => setEditing(null)}
          onSave={(payload) => save(payload, editing === "new")}
        />
      )}

      {assignFor && (
        <AssignBadgeDialog
          badge={assignFor}
          onClose={() => { setAssignFor(null); reload(); }}
        />
      )}
    </div>
  );
}


function BadgeEditor({ initial, onClose, onSave }) {
  const [form, setForm] = useState(initial);
  const set = (k, v) => setForm({ ...form, [k]: v });
  const submit = (e) => {
    e?.preventDefault?.();
    if (!form.name?.trim()) { toast.error("Name is required."); return; }
    onSave({
      name: form.name.trim(),
      description: form.description?.trim() || "",
      icon: form.icon || "✨",
      color: form.color || "#C9A055",
      rarity: form.rarity || "common",
    });
  };

  return (
    <div
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="badge-editor-overlay"
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="bg-[#0a0a0a] border border-white/10 max-w-lg w-full max-h-[90vh] overflow-y-auto"
        data-testid="badge-editor-form"
      >
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <div className="font-display text-xl text-[#F4F1EA]">{initial?.id ? "Edit Badge" : "New Badge"}</div>
          <button type="button" onClick={onClose} className="text-[#8a857d] hover:text-[#F4F1EA]"><X size={18} /></button>
        </div>

        <div className="p-5 space-y-4">
          {/* Live preview */}
          <div className={`flex items-center gap-3 p-3 border ${RARITY_BORDER[form.rarity] || "border-white/15"}`}>
            <div
              className="w-14 h-14 flex items-center justify-center text-2xl rounded-full"
              style={{ background: `${form.color}22`, border: `1px solid ${form.color}` }}
            >
              <span>{form.icon || "✨"}</span>
            </div>
            <div>
              <div className="font-display text-base text-[#F4F1EA]">{form.name || "Badge name"}</div>
              <div className="font-ui text-[9px] tracking-[0.22em] uppercase text-[#8a857d] mt-0.5">{form.rarity}</div>
            </div>
          </div>

          <Field label="Name">
            <input
              data-testid="badge-form-name"
              className="stc-input"
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              maxLength={80}
              placeholder="Truth Pioneer"
            />
          </Field>

          <Field label="Description">
            <textarea
              data-testid="badge-form-description"
              className="stc-input min-h-[80px]"
              value={form.description}
              onChange={(e) => set("description", e.target.value)}
              maxLength={500}
              placeholder="Awarded for..."
            />
          </Field>

          <div className="grid grid-cols-3 gap-3">
            <Field label="Icon">
              <input
                data-testid="badge-form-icon"
                className="stc-input text-center text-xl"
                value={form.icon}
                onChange={(e) => set("icon", e.target.value)}
                maxLength={4}
              />
            </Field>
            <Field label="Color">
              <input
                data-testid="badge-form-color"
                type="color"
                className="stc-input h-10 p-0"
                value={form.color}
                onChange={(e) => set("color", e.target.value)}
              />
            </Field>
            <Field label="Rarity">
              <select
                data-testid="badge-form-rarity"
                className="stc-input"
                value={form.rarity}
                onChange={(e) => set("rarity", e.target.value)}
              >
                {RARITIES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </Field>
          </div>
        </div>

        <div className="flex justify-end gap-2 p-5 border-t border-white/10">
          <button type="button" onClick={onClose} className="btn-outline">Cancel</button>
          <button type="submit" className="btn-gold" data-testid="badge-form-save">
            {initial?.id ? "Save Changes" : "Create Badge"}
          </button>
        </div>
      </form>
    </div>
  );
}


function Field({ label, children }) {
  return (
    <label className="block">
      <div className="font-ui text-[10px] tracking-[0.22em] uppercase text-[#8a857d] mb-1.5">{label}</div>
      {children}
    </label>
  );
}


function AssignBadgeDialog({ badge, onClose }) {
  const [q, setQ] = useState("");
  const [customers, setCustomers] = useState([]);
  const [assigned, setAssigned] = useState(new Set());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await http.get("/admin/customers");
        setCustomers(data || []);
      } catch {
        toast.error("Couldn't load customers.");
      }
    })();
  }, []);

  const filtered = customers.filter(u => {
    if (!q.trim()) return true;
    const s = q.trim().toLowerCase();
    return (u.name || "").toLowerCase().includes(s) || (u.email || "").toLowerCase().includes(s);
  }).slice(0, 50);

  const toggle = async (user) => {
    if (busy) return;
    setBusy(true);
    try {
      if (assigned.has(user.id)) {
        await http.delete(`/admin/users/${user.id}/badges/${badge.id}`);
        const next = new Set(assigned); next.delete(user.id);
        setAssigned(next);
        toast.success(`Removed from ${user.email}`);
      } else {
        await http.post(`/admin/users/${user.id}/badges`, { badge_id: badge.id });
        const next = new Set(assigned); next.add(user.id);
        setAssigned(next);
        toast.success(`Assigned to ${user.email}`);
      }
    } catch {
      toast.error("Couldn't update.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="badge-assign-overlay"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-[#0a0a0a] border border-white/10 max-w-lg w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 flex items-center justify-center text-xl rounded-full"
              style={{ background: `${badge.color}22`, border: `1px solid ${badge.color}` }}
            >
              {badge.icon}
            </div>
            <div>
              <div className="font-display text-base text-[#F4F1EA]">Assign: {badge.name}</div>
              <div className="font-ui text-[9px] tracking-[0.22em] uppercase text-[#8a857d] mt-0.5">
                Tap a customer to toggle
              </div>
            </div>
          </div>
          <button onClick={onClose} className="text-[#8a857d] hover:text-[#F4F1EA]"><X size={18} /></button>
        </div>

        <div className="p-5 border-b border-white/10">
          <input
            data-testid="badge-assign-search"
            className="stc-input"
            placeholder="Search by name or email…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>

        <div className="max-h-[50vh] overflow-y-auto" data-testid="badge-assign-list">
          {filtered.length === 0 ? (
            <div className="p-6 text-[#8a857d] text-sm font-body text-center">No customers match.</div>
          ) : (
            filtered.map(u => {
              const isOn = assigned.has(u.id);
              return (
                <button
                  key={u.id}
                  type="button"
                  onClick={() => toggle(u)}
                  data-testid={`badge-assign-row-${u.email}`}
                  className={`w-full flex items-center justify-between px-4 py-3 border-b border-white/5 text-left hover:bg-white/5 ${isOn ? "bg-[#C9A055]/10" : ""}`}
                >
                  <div className="min-w-0">
                    <div className="font-body text-sm text-[#F4F1EA] truncate">{u.name || "—"}</div>
                    <div className="font-ui text-[11px] text-[#8a857d] truncate">{u.email}</div>
                  </div>
                  <span className={`font-ui text-[10px] tracking-[0.22em] uppercase ${isOn ? "text-[#C9A055]" : "text-[#8a857d]"}`}>
                    {isOn ? "Assigned ✓" : "Assign"}
                  </span>
                </button>
              );
            })
          )}
        </div>

        <div className="p-4 border-t border-white/10 text-center">
          <button onClick={onClose} className="btn-outline">Done</button>
        </div>
      </div>
    </div>
  );
}
